import { useState, useEffect, useRef, useCallback } from "react";
import useStatusParam from "../../hooks/useStatusParam";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import ConfirmDialog from "../../app-components/ui/ConfirmDialog";
import ActiveFilters, {
  FilterChip,
} from "../../app-components/ui/ActiveFilters";
import Avatar from "../../app-components/ui/Avatar";
import ColumnMenu, {
  ColumnDef,
  useColumnVisibility,
} from "../../app-components/ui/ColumnMenu";
import DateFilter from "../../app-components/ui/DateFilter";
import EmptyState from "../../app-components/ui/EmptyState";
import PageHeader from "../../app-components/ui/PageHeader";
import Pagination, { PER_PAGE_OPTIONS } from "../../app-components/ui/Pagination";
import StatusPill from "../../app-components/ui/StatusPill";
import {
  btnPrimary,
  card,
  inputBase,
} from "../../app-components/ui/styles";
import TableSkeleton from "../../app-components/ui/TableSkeleton";
import { ParamInterface, UserInterface } from "../../interface/users.interface";
import {
  formatDate,
  FUNNEL_LEGEND,
  getErrorMessage,
  getFunnelStage,
} from "../../utils";
import notify from "../../utils/notify";
import { downloadCsvExport } from "../../utils/exportCsv";
import ExportButton from "../../app-components/ui/ExportButton";
import UserRowActions from "./components/UserRowActions";

const AGE_OPTIONS = [
  { value: "18,24", label: "18 - 24" },
  { value: "25,40", label: "25 - 40" },
  { value: "41,50", label: "41 - 50" },
  { value: "51,60", label: "51 - 60" },
  { value: "60,100", label: "60 and above" },
];

const TABS = [
  { value: "", label: "All users" },
  { value: "PENDING", label: "Pending" },
  { value: "ACTIVE", label: "Active" },
  { value: "FLAGGED", label: "Flagged" },
  { value: "REMOVED", label: "Removed" },
];

// Optional columns the admin can show or hide. Stage, Name and Action are always shown.
const COLUMNS: ColumnDef[] = [
  { key: "profilePhoto", label: "Profile Photo" },
  { key: "email", label: "LinkedIn Email" },
  { key: "username", label: "Username", defaultVisible: false },
  { key: "uniqueNo", label: "Unique No", defaultVisible: false },
  { key: "phone", label: "Phone" },
  { key: "phoneVerified", label: "Phone Verified", defaultVisible: false },
  { key: "country", label: "Country" },
  { key: "state", label: "State" },
  { key: "date", label: "Request Date" },
  { key: "gender", label: "Gender", defaultVisible: false },
  { key: "status", label: "Status" },
];

const th =
  "py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-hcMuted bg-hcHead whitespace-nowrap";
const td = "py-3 px-4 text-left text-sm whitespace-nowrap";

const PER_PAGE_KEY = "hc_per_page_users";

// Remembered rows-per-page; never below 25
const readPerPage = () => {
  try {
    const n = Number(localStorage.getItem(PER_PAGE_KEY));
    return PER_PAGE_OPTIONS.includes(n) ? n : PER_PAGE_OPTIONS[0];
  } catch {
    return PER_PAGE_OPTIONS[0];
  }
};

type UserListParams = ParamInterface & { per_page?: number };

const EliteUser = () => {
  const [usersData, setUsersData] = useState<UserInterface[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [perPage, setPerPage] = useState<number>(readPerPage);
  // page size the API actually used (Laravel returns per_page); used for the "1–25 of N" range
  const [pagePerPage, setPagePerPage] = useState<number | undefined>(undefined);
  // status tab lives in the URL (?status=...) so sidebar submenus can link to it
  const [pageFilter, setPageFilter] = useStatusParam();
  // back to page 1 whenever the status changes (tab click or sidebar link)
  useEffect(() => setCurrentPage(1), [pageFilter]);

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchInputDebounce, setSearchInputDebounce] = useState<string>("");
  const [ageRange, setAgeRange] = useState<string>("");
  const [dateRange, setDateRange] = useState<string>("");
  const [subscriptionPlan] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // inline messages instead of the old floating red box
  const [dateError, setDateError] = useState("");
  const [loadError, setLoadError] = useState("");
  // rows with a status change in flight (locked so they can't be double-clicked)
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
  const [confirmRemove, setConfirmRemove] = useState<UserInterface | null>(null);
  // only the latest list request may update the table (fast filter changes)
  const requestSeq = useRef(0);

  const { isVisible, toggle, visibleCount } = useColumnVisibility(
    "cols_elite_users",
    COLUMNS
  );
  const show = (key: string) => isVisible(key);

  const listParams = useCallback(
    (): UserListParams => ({
      page: currentPage,
      per_page: perPage,
      status: pageFilter,
      search: searchInput,
      ageRange: ageRange,
      dateRange: dateRange,
      subscriptionPlan: subscriptionPlan,
    }),
    [currentPage, perPage, pageFilter, searchInput, ageRange, dateRange, subscriptionPlan]
  );

  // effect for get users list
  useEffect(() => {
    getUsers(listParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    perPage,
    pageFilter,
    searchInput,
    ageRange,
    dateRange,
    subscriptionPlan,
  ]);

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchInput(searchInputDebounce);
      setCurrentPage(1);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInputDebounce]);

  useEffect(() => {
    setDateError("");

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end >= start) {
        setDateRange(startDate + "," + endDate);
        setCurrentPage(1);
      } else {
        setDateError("End date must be on or after the start date.");
      }
    }
    if (!startDate && !endDate) {
      setDateRange("");
    }
  }, [startDate, endDate]);

  /** silent: refresh in the background without the loading skeleton */
  const getUsers = async (params: UserListParams, silent = false) => {
    const seq = ++requestSeq.current;
    if (!silent) setLoading(true);
    setLoadError("");
    try {
      const res: any = await apiClient.get("elite-users", { params });
      if (seq !== requestSeq.current) return; // a newer request has replaced this one
      if (res && res.data) {
        setUsersData(res.data.data ?? []);
        setCurrentPage(res.data.current_page);
        setLastPage(res.data.last_page);
        setTotal(res.data.total);
        const used = Number(res.data.per_page);
        setPagePerPage(Number.isFinite(used) && used > 0 ? used : undefined);
      } else {
        setUsersData([]);
      }
    } catch (error) {
      if (seq !== requestSeq.current) return;
      setLoadError(getErrorMessage(error));
      if (!silent) setUsersData([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  const displayName = (u: UserInterface) =>
    u.linkedin_full_name || u.full_name || u.username || "This user";

  const setBusy = (id: number, on: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  /** Approve / remove: no optimistic change, so the table only shows what the server saved */
  const updateStatus = async (user: UserInterface, status: "APPROVED" | "REMOVED") => {
    const name = displayName(user);
    setBusy(user.id, true);
    try {
      const res: any = await apiClient.patch(`elite-users/${user.id}/status`, { status });
      const saved = res?.data?.user;
      setUsersData((rows) =>
        rows.map((u) => (u.id === user.id ? (saved ?? { ...u, elite_status: status }) : u))
      );
      // the API reports which notifications (push / in_app / email) didn't go out
      const sent: Record<string, boolean> = res?.data?.notifications ?? {};
      const labels: Record<string, string> = { push: "push notification", in_app: "in-app notification", email: "email" };
      const failed = Object.keys(sent).filter((k) => !sent[k]).map((k) => labels[k] ?? k);
      if (status === "APPROVED" && failed.length) {
        notify.warning(`${name} approved, but the ${failed.join(" and ")} with their code didn't go out.`);
      } else if (status === "APPROVED" && res?.data?.message?.includes("already approved")) {
        notify.info(`${name} was already approved. Their code is unchanged.`);
      } else {
        notify.success(status === "APPROVED" ? `${name} approved.` : `${name} removed.`);
      }
      // refresh so the user moves to the right tab and counts stay correct
      getUsers(listParams(), true);
      return true;
    } catch (error) {
      notify.error(
        `Couldn't ${status === "APPROVED" ? "approve" : "remove"} ${name}: ${getErrorMessage(error)}`
      );
      return false;
    } finally {
      setBusy(user.id, false);
    }
  };

  const confirmAndRemove = async () => {
    if (!confirmRemove) return;
    const ok = await updateStatus(confirmRemove, "REMOVED");
    if (ok) setConfirmRemove(null);
  };

  const setDates = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const clearFilters = () => {
    setSearchInputDebounce("");
    setSearchInput("");
    setAgeRange("");
    setDates("", "");
    setCurrentPage(1);
  };

  // Chips for every filter currently applied
  const chips: FilterChip[] = [];
  if (searchInput)
    chips.push({
      label: `Search: ${searchInput}`,
      onRemove: () => {
        setSearchInputDebounce("");
        setSearchInput("");
        setCurrentPage(1);
      },
    });
  if (ageRange)
    chips.push({
      label: `Age: ${
        AGE_OPTIONS.find((a) => a.value === ageRange)?.label ?? ageRange
      }`,
      onRemove: () => {
        setAgeRange("");
        setCurrentPage(1);
      },
    });
  if (dateRange)
    chips.push({
      label: `Date: ${startDate} to ${endDate}`,
      onRemove: () => setDates("", ""),
    });

  const hasRecords = usersData?.length > 0;

  const rowsPerPage = pagePerPage ?? perPage;

  const changePerPage = (n: number) => {
    try {
      localStorage.setItem(PER_PAGE_KEY, String(n));
    } catch {
      /* not remembered if storage is unavailable */
    }
    setPerPage(n);
    setCurrentPage(1);
  };

  return (
    <AdminLayout title="Admin | Users">
      <PageHeader
        title="Users"
        subtitle="Review and approve elite member requests"
        actions={
          <ExportButton
            total={total}
            noun="users"
            disabled={loading}
            onExport={() => downloadCsvExport("elite-users/export", { ...listParams() }, "elite-users", total, "users")}
          />
        }
      />

      <div className={`${card} py-5`}>
        {/* tabs */}
        <div className="px-5 flex items-center gap-1 mb-5 border-b border-hcBorder">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => {
                setPageFilter(t.value);
                setCurrentPage(1);
              }}
              className={`px-4 pb-3 text-sm font-medium -mb-px border-b-2 transition ${
                pageFilter === t.value
                  ? "text-honeConnectPurple border-honeConnectPurple"
                  : "text-honeConnectGray border-transparent hover:text-honeConnectBlack"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* filter bar */}
        <div className="px-5 flex items-center gap-3 flex-wrap mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
            </span>
            <input
              value={searchInputDebounce}
              onChange={(e) => setSearchInputDebounce(e.target.value)}
              type="text"
              className={`${inputBase} pl-9 w-full`}
              placeholder="Search by name, username, unique number, location or interest"
            />
          </div>

          <select
            aria-label="Age"
            className={inputBase}
            value={ageRange}
            onChange={(e) => {
              setAgeRange(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All ages</option>
            {AGE_OPTIONS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>

          <ColumnMenu columns={COLUMNS} isVisible={isVisible} onToggle={toggle} />
        </div>

        <div className="px-5 mb-4">
          <DateFilter
            startDate={startDate}
            endDate={endDate}
            onChange={setDates}
          />
          {dateError && (
            <p role="alert" className="mt-2 text-sm text-red-600">
              {dateError}
            </p>
          )}
        </div>

        <ActiveFilters chips={chips} onClearAll={clearFilters} />

        {/* funnel legend */}
        <div className="px-5 flex items-center gap-4 mb-4 flex-wrap text-xs text-honeConnectGray">
          {FUNNEL_LEGEND.map((l) => (
            <span key={l.label} className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: l.color }}
              />
              {l.label}
            </span>
          ))}
        </div>

        {/* record table */}
        {loadError && !loading && !hasRecords ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center border-y border-hcBorder">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <svg
                className="w-7 h-7 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="font-semibold text-honeConnectBlack mb-1">
              Couldn't load users
            </p>
            <p className="text-sm text-hcMuted mb-4 max-w-md">{loadError}</p>
            <button onClick={() => getUsers(listParams())} className={btnPrimary}>
              Try again
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto border-y border-hcBorder">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th className={th}>Stage</th>
                  <th
                    className={`${th} left-0 z-20`}
                    style={{ position: "sticky" }}
                  >
                    LinkedIn Name
                  </th>
                  {show("profilePhoto") && (
                    <th className={th}>Profile Photo</th>
                  )}
                  {show("email") && <th className={th}>LinkedIn Email</th>}
                  {show("username") && <th className={th}>Username</th>}
                  {show("uniqueNo") && <th className={th}>Unique No</th>}
                  {show("phone") && <th className={th}>Phone</th>}
                  {show("phoneVerified") && (
                    <th className={th}>Phone Verified</th>
                  )}
                  {show("country") && <th className={th}>Country</th>}
                  {show("state") && <th className={th}>State</th>}
                  {show("date") && <th className={th}>Request Date</th>}
                  {show("gender") && <th className={th}>Gender</th>}
                  {show("status") && <th className={th}>Status</th>}
                  <th className={`${th} text-center`}>Action</th>
                </tr>
              </thead>

              {loading ? (
                <TableSkeleton columns={visibleCount + 3} />
              ) : (
                <tbody className="text-honeConnectBlack">
                  {usersData?.map((user: UserInterface, i: number) => (
                    <tr
                      key={user.id}
                      className="group border-b border-hcBorder hover:bg-hcBg"
                    >
                      <td className={td}>
                        <span
                          title={getFunnelStage(user).label}
                          className="inline-block w-2.5 h-2.5 rounded-full"
                          style={{
                            backgroundColor: getFunnelStage(user).color,
                          }}
                        />
                      </td>

                      <td
                        className={`${td} font-medium bg-white group-hover:bg-hcBg z-[1]`}
                        style={{ position: "sticky", left: 0 }}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar
                            name={user.linkedin_full_name}
                            src={user.linkedin_photo}
                          />
                          <span>{user.linkedin_full_name ?? "no name"}</span>
                        </div>
                      </td>

                      {show("profilePhoto") && (
                        <td className={td}>
                          <Avatar
                            name={user.full_name}
                            src={user.profile_pix_link}
                          />
                        </td>
                      )}

                      {show("email") && (
                        <td className={td}>
                          {user.linkedin_email ?? "no email"}
                        </td>
                      )}

                      {show("username") && (
                        <td className={td}>{user.username ?? "no username"}</td>
                      )}

                      {show("uniqueNo") && (
                        <td className={td}>
                          {user.id.toString().padStart(6, "0")}
                        </td>
                      )}

                      {show("phone") && (
                        <td className={td}>
                          {user.phone_number ?? "no phone"}
                        </td>
                      )}

                      {show("phoneVerified") && (
                        <td className={td}>
                          <StatusPill
                            label={user?.is_phone_verified ? "Yes" : "No"}
                          />
                        </td>
                      )}

                      {show("country") && (
                        <td className={td}>{user.country?.name ?? "-"}</td>
                      )}

                      {show("state") && (
                        <td className={td}>{user.state?.name ?? "-"}</td>
                      )}

                      {show("date") && (
                        <td className={td}>
                          {user.created_at
                            ? formatDate(user.created_at, "date")
                            : ""}
                        </td>
                      )}

                      {show("gender") && (
                        <td className={`${td} capitalize`}>{user?.gender}</td>
                      )}

                      {show("status") && (
                        <td className={td}>
                          {user.elite_status ? (
                            <StatusPill label={user.elite_status} />
                          ) : (
                            <StatusPill label="N/A" tone="neutral" />
                          )}
                        </td>
                      )}

                      <td className={`${td} text-center`}>
                        <UserRowActions
                          userId={user.id}
                          eliteStatus={user.elite_status}
                          busy={busyIds.has(user.id)}
                          openUp={usersData.length > 3 && i >= usersData.length - 2}
                          onApprove={() => updateStatus(user, "APPROVED")}
                          onRemove={() => setConfirmRemove(user)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>

            {!loading && !hasRecords && (
              <EmptyState
                title={
                  chips.length
                    ? "No users match these filters"
                    : pageFilter
                    ? `No ${(TABS.find((t) => t.value === pageFilter)?.label ?? pageFilter).toLowerCase()} users`
                    : "No users yet"
                }
                message={
                  chips.length
                    ? "Try changing your search or filters."
                    : pageFilter
                    ? "There are no users with this status right now."
                    : "New members will appear here when they sign up."
                }
                onClear={chips.length ? clearFilters : undefined}
              />
            )}
          </div>
        )}

        {hasRecords && (
          <Pagination
            currentPage={currentPage}
            lastPage={lastPage}
            total={total}
            onPageChange={setCurrentPage}
            perPage={perPage}
            pageSize={rowsPerPage}
            onPerPageChange={changePerPage}
          />
        )}
      </div>

      <ConfirmDialog
        open={!!confirmRemove}
        title={`Remove ${confirmRemove ? displayName(confirmRemove) : "this user"}?`}
        message="They will lose their elite membership. You can approve them again later if needed."
        confirmLabel="Remove user"
        loading={!!confirmRemove && busyIds.has(confirmRemove.id)}
        onConfirm={confirmAndRemove}
        onCancel={() => setConfirmRemove(null)}
      />
    </AdminLayout>
  );
};

export default EliteUser;
